/**
 * AuthService.js
 * 
 * Service for handling authentication and user management.
 * Uses minimal server-side authentication for premium subscription.
 */

class AuthService {
  constructor() {
    this.apiUrl = process.env.REACT_APP_AUTH_API_URL || '/api/auth';
  }
  
  /**
   * Authenticate a user with credentials
   * @param {Object} credentials - User credentials
   * @param {string} credentials.email - User email
   * @param {string} credentials.password - User password
   * @returns {Promise<Object>} User data with token
   */
  async login(credentials) {
    try {
      const response = await fetch(`${this.apiUrl}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(credentials)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Authentication failed');
      }
      
      const data = await response.json();
      
      // Store auth data in localStorage
      localStorage.setItem('authToken', data.token);
      localStorage.setItem('user', JSON.stringify({
        id: data.userId,
        name: data.name,
        email: data.email,
        subscription: data.subscription
      }));
      
      return {
        id: data.userId,
        name: data.name,
        email: data.email,
        subscription: data.subscription
      };
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }
  
  /**
   * Register a new user
   * @param {Object} userData - User registration data
   * @returns {Promise<Object>} User data with token
   */
  async register(userData) {
    try {
      const response = await fetch(`${this.apiUrl}/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Registration failed');
      }
      
      const data = await response.json();
      
      // Store auth data in localStorage
      localStorage.setItem('authToken', data.token);
      localStorage.setItem('user', JSON.stringify({
        id: data.userId,
        name: data.name,
        email: data.email,
        subscription: data.subscription || 'free'
      }));
      
      return {
        id: data.userId,
        name: data.name,
        email: data.email,
        subscription: data.subscription || 'free'
      };
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  }
  
  /**
   * Log out the current user
   */
  logout() {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
  }
  
  /**
   * Check if user is authenticated
   * @returns {boolean} Authentication status
   */
  isAuthenticated() {
    const token = localStorage.getItem('authToken');
    return !!token;
  }
  
  /**
   * Get current user data
   * @returns {Object|null} Current user data or null
   */
  getCurrentUser() {
    const userString = localStorage.getItem('user');
    return userString ? JSON.parse(userString) : null;
  }
  
  /**
   * Update user subscription status
   * @param {Object} subscriptionDetails - Subscription data
   * @returns {Promise<Object>} Updated subscription info
   */
  async updateSubscription(subscriptionDetails) {
    try {
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      const response = await fetch(`${this.apiUrl}/subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(subscriptionDetails)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update subscription');
      }
      
      const data = await response.json();
      
      // Update user in localStorage
      const user = this.getCurrentUser();
      localStorage.setItem('user', JSON.stringify({
        ...user,
        subscription: data.subscription
      }));
      
      return {
        success: true,
        subscription: data.subscription
      };
    } catch (error) {
      console.error('Subscription update error:', error);
      throw error;
    }
  }
  
  /**
   * Get user profile data
   * @returns {Promise<Object>} User profile data
   */
  async getUserProfile() {
    try {
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      const response = await fetch(`${this.apiUrl}/profile`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to get profile');
      }
      
      const data = await response.json();
      
      // Update user in localStorage to keep it in sync
      localStorage.setItem('user', JSON.stringify({
        id: data.userId,
        name: data.name,
        email: data.email,
        subscription: data.subscription
      }));
      
      return data;
    } catch (error) {
      console.error('Profile fetch error:', error);
      throw error;
    }
  }
  
  /**
   * Update user profile data
   * @param {Object} profileData - Updated profile data
   * @returns {Promise<Object>} Updated profile
   */
  async updateProfile(profileData) {
    try {
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      const response = await fetch(`${this.apiUrl}/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(profileData)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update profile');
      }
      
      const data = await response.json();
      
      // Update user in localStorage
      const user = this.getCurrentUser();
      localStorage.setItem('user', JSON.stringify({
        ...user,
        name: data.name,
        email: data.email
      }));
      
      return data;
    } catch (error) {
      console.error('Profile update error:', error);
      throw error;
    }
  }
  
  /**
   * Change user password
   * @param {Object} passwordData - Password change data
   * @param {string} passwordData.currentPassword - Current password
   * @param {string} passwordData.newPassword - New password
   * @returns {Promise<Object>} Result object
   */
  async changePassword(passwordData) {
    try {
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      const response = await fetch(`${this.apiUrl}/password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(passwordData)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to change password');
      }
      
      return { success: true };
    } catch (error) {
      console.error('Password change error:', error);
      throw error;
    }
  }
}

export default AuthService;
